/**
 * Floor Actions
 * Core floor CRUD operations: add, delete, setCurrentFloor, update, etc.
 */

import { generateId, getCurrentTimestamp } from '../../lib/utils';
import { log, LogCategory } from '../../lib/logger';
import { FloorId, ProjectId } from '../../types/branded';
import type { Floor, FloorState, MapData, MapDataPatch } from './types';
import { persistState, migrateMapData, initializeMapData, loadInitialState } from './persistence';

/**
 * Generate floor name based on order
 * Negative order = basement (B1, B2, ...)
 * 0 = ground floor (1F)
 * Positive = upper floors (2F, 3F, ...)
 */
const generateFloorName = (order: number): string => {
  if (order < 0) {
    return `B${Math.abs(order)}`;
  } else {
    return `${order + 1}F`;
  }
};

/**
 * Calculate the next floor order for a lot
 * All floors are basement levels (B1 -> B2 -> B3 -> B4...)
 */
const calculateNextOrder = (floors: Floor[], lotId: string): number => {
  const lotFloors = floors.filter((f) => f.lotId === lotId);
  if (lotFloors.length === 0) {
    return -1; // Start with B1 (basement level 1)
  }

  // Get the lowest order (most negative) and go one level deeper
  const minOrder = Math.min(...lotFloors.map((f) => f.order));
  return minOrder - 1; // -1 -> -2 (B1 -> B2), -2 -> -3 (B2 -> B3), etc.
};

type SetFn = (partial: Partial<FloorState>) => void;
type GetFn = () => FloorState;

export const createFloorActions = (set: SetFn, get: GetFn) => ({
  setFloorSwitching: (value: boolean) => {
    set({ isFloorSwitching: value });
  },

  addFloor: (lotId: ProjectId | string, floor: Omit<Floor, 'id' | 'lotId' | 'created' | 'modified' | 'order' | 'mapData'> & { name?: string } = {} as any) => {
    const state = get();
    const now = getCurrentTimestamp();

    // Cast to branded types
    const lotIdBranded = lotId as ProjectId;

    // Calculate order. Use caller-supplied name when provided (e.g. user input
    // from the create-project modal); otherwise fall back to auto-generated.
    const order = calculateNextOrder(state.floors, lotIdBranded);
    const name = floor?.name && floor.name.trim().length > 0
      ? floor.name.trim()
      : generateFloorName(order);

    // FIX: New floors should start empty to allow CSV/DXF upload UI to be visible
    // Users expect to upload files for each floor individually
    // If users want the same CSV data on all floors, they can upload once and copy manually
    const initialMapData: MapData = initializeMapData();

    const newFloor: Floor = {
      ...floor,
      id: generateId() as FloorId,
      lotId: lotIdBranded,
      name,
      order,
      mapData: initialMapData,
      created: now,
      modified: now,
    };

    const newFloors = [...state.floors, newFloor];
    const newState = {
      floors: newFloors,
      currentFloor: newFloor.id,
    };

    // Persist to localStorage
    persistState(newFloors, newFloor.id);

    set(newState);
  },

  reassignLotId: (oldLotId: ProjectId | string, newLotId: ProjectId | string) => {
    if (!oldLotId || !newLotId || oldLotId === newLotId) return;
    const state = get();
    const oldStr = String(oldLotId);
    const newStr = String(newLotId);
    let touched = 0;
    const newFloors = state.floors.map((f) => {
      if (String(f.lotId) === oldStr) {
        touched += 1;
        return { ...f, lotId: newStr as ProjectId, modified: getCurrentTimestamp() };
      }
      return f;
    });
    if (touched === 0) return;

    log.info(LogCategory.STATE, 'reassignLotId: cascading lot id change', {
      from: oldStr,
      to: newStr,
      floors: touched,
    });

    persistState(newFloors, state.currentFloor);
    set({ floors: newFloors });
  },

  deleteFloor: (id: FloorId | string) => {
    const state = get();
    const newFloors = state.floors.filter((f) => f.id !== id);

    // If we're deleting the current floor, clear it or select another
    let newCurrentFloor = state.currentFloor;
    if (state.currentFloor === id) {
      // Try to select the first remaining floor with the same lotId
      const deletedFloor = state.floors.find((f) => f.id === id);
      if (deletedFloor) {
        const remainingFloors = newFloors.filter((f) => f.lotId === deletedFloor.lotId);
        newCurrentFloor = remainingFloors.length > 0 ? remainingFloors[0].id : null;
      } else {
        newCurrentFloor = null;
      }
    }

    const newState = {
      floors: newFloors,
      currentFloor: newCurrentFloor,
    };

    // Persist to localStorage
    persistState(newFloors, newCurrentFloor);

    set(newState);

    // Clean up related mappings from objectTypeStore
    // Import is circular, so use dynamic import to avoid issues
    import('../objectTypeStore').then(({ useObjectTypeStore }) => {
      const removeMappingsByFloorId = useObjectTypeStore.getState().removeMappingsByFloorId;
      removeMappingsByFloorId(id);
      log.info(LogCategory.FLOOR, `Deleted floor ${id} and its mappings`);
    });
  },

  setCurrentFloor: (id: FloorId | string | null) => {
    const state = get();

    // Validate that the floor exists
    if (id !== null && !state.floors.find((f) => f.id === id)) {
      throw new Error(`Floor with id "${id}" not found`);
    }

    // Persist to localStorage
    persistState(state.floors, id as FloorId | null);

    set({ currentFloor: id as FloorId | null });
  },

  getFloorsByLotId: (lotId: ProjectId | string) => {
    // Return floors sorted by order (basement floors first, then ground and upper)
    // Use String() comparison to handle potential type mismatches (string vs number)
    return get()
      .floors.filter((f) => String(f.lotId) === String(lotId))
      .sort((a, b) => a.order - b.order);
  },

  updateFloor: (id: FloorId | string, updates: Partial<Floor>) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === id);

    if (!floor) {
      throw new Error(`Floor with id "${id}" not found`);
    }

    const updatedFloor: Floor = {
      ...floor,
      ...updates,
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === id ? updatedFloor : f));

    // Persist to localStorage
    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });
  },

  updateFloorMapData: (id: FloorId | string, mapData: MapDataPatch) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === id);

    if (!floor) {
      throw new Error(`Floor with id "${id}" not found`);
    }

    const mergedMap = {
      ...(floor.mapData ?? {}),
      ...mapData,
    } as MapData | Record<string, unknown>;

    const updatedFloor: Floor = {
      ...floor,
      mapData: migrateMapData(mergedMap),
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === id ? updatedFloor : f));

    // Persist to localStorage
    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });
  },

  getCurrentFloorData: () => {
    const state = get();
    if (!state.currentFloor) return undefined;
    return state.floors.find((f) => f.id === state.currentFloor);
  },

  reloadFromStorage: () => {
    const stored = loadInitialState();
    log.info(LogCategory.FLOOR, 'Reloading from storage', {
      floorsCount: stored.floors.length,
      currentFloor: stored.currentFloor,
    });
    set({
      floors: stored.floors,
      currentFloor: stored.currentFloor,
    });
  },
});
